import { Theme, createTheme, darken } from "@mui/material";
import { LinkProps } from "@mui/material/Link";

import { LinkBehavior } from "../behaviors";
import { scrollBarWidth } from "../constants";
import { palette, themeColors } from "../palette";
import { semanticColors } from "../tokens/semanticColors";

const borderRadius: Record<string, number> = {
  small: 4,
  medium: 8,
  large: 12,
  button: 26,
};

const openSansFontFamily = '"Open Sans", Inter, "Segoe UI", Arial, sans-serif';
const sourceSansFontFamily = '"SourceSans3", Inter, "Segoe UI", Arial, sans-serif';

const widgetAnalysisLayoutOverrides = {
  containerGap: "14px",
  panelPadding: "20px",
  panelGap: "20px",
  sectionGap: "14px",
  compactGap: "14px",
  titleGap: "16px",
  cardPaddingCompact: "10px 14px",
  cardPadding: "10px 14px",
  cardGap: "8px",
  bodyFontSize: "14px",
  titleFontSize: "16px",
  headerIconSize: "40px",
  cardIconSize: "40px",
  demandScoreValueFontSize: "16px",
  demandScoreLabelFontSize: "12px",
  customChipFontSize: "10px",
  refinementInfoIconSize: "18px",
  buttonHeight: "34px",
  buttonPadding: "7px 14px",
  inputHeight: "32px",
};

export const LightTheme = (...args: object[]): Theme =>
  createTheme(
    {
      semanticColors,
      themeColors,
      breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl", "xxl"],
        values: { xs: 0, sm: 600, md: 1024, lg: 1280, xl: 1536, xxl: 1900 },
      },
      palette,
      analysisLayout: {
        containerGap: "30px",
        panelPadding: "30px",
        panelGap: "30px",
        sectionGap: "20px",
        compactGap: "16px",
        titleGap: "20px",
        cardPaddingCompact: "20px 8px",
        cardPadding: "20px",
        cardGap: "18px",
        bodyFontSize: "16px",
        titleFontSize: "20px",
        headerIconSize: "45px",
        cardIconSize: "50px",
        demandScoreValueFontSize: "24px",
        demandScoreLabelFontSize: "14px",
        customChipFontSize: "12px",
        refinementInfoIconSize: "20px",
        buttonHeight: "40px",
        buttonPadding: "8px 20px",
        inputHeight: "40px",
      },
      sidebar: {
        background: semanticColors.sidebar.background,
        textColor: semanticColors.sidebar.text,
        selectedTextColor: semanticColors.sidebar.selectedText,
        selectedBackgroundStartColor: semanticColors.sidebar.selectedBackgroundStart,
        selectedBackgroundColor: semanticColors.sidebar.selectedBackground,
        drawerWidth: 100,
        fontFamily: openSansFontFamily,
        fontWeight: "700",
        fontSize: "11px",
        lineHeight: "160%",
        letterSpacing: "-0.011em",
      },
      typography: {
        fontFamily: openSansFontFamily,
        h1: {
          color: themeColors.schwarzgrau900,
          fontWeight: "700",
          fontSize: "24px",
          lineHeight: "28px",
        },
        h2: {
          color: themeColors.schwarzgrau900,
        },
        h3: {
          fontSize: "34px",
          color: themeColors.schwarzgrau500,
        },
        h4: {
          color: themeColors.schwarzgrau500,
          fontWeight: "600",
          fontSize: "18px",
          lineHeight: "24px",
        },
        h5: {
          color: themeColors.schwarzgrau900,
        },
        h6: {
          color: themeColors.schwarzgrau900,
        },
        pageTitle: {
          color: themeColors.schwarzgrau500,
          fontWeight: 600,
          fontSize: "24px",
          lineHeight: 1.5,
        },
        widgetTitle: {
          color: themeColors.schwarzgrau900,
          fontWeight: 600,
          fontSize: "20px",
          lineHeight: 1.5,
        },
        sectionHeader: {
          color: themeColors.schwarzgrau900,
          fontWeight: 400,
          fontSize: "20px",
          lineHeight: 1.4,
        },
        panelDescription: {
          color: themeColors.schwarzgrau500,
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: 1.5,
          letterSpacing: "-0.011em",
        },
        modalHeader: {
          fontWeight: 600,
          fontSize: "20px",
          color: themeColors.schwarzgrau500,
          lineHeight: 1.5,
        },
        modalTitle: {
          fontWeight: 400,
          fontSize: "24px",
          lineHeight: 1.5,
        },
        inputLabel: {
          color: themeColors.schwarzgrau500,
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: 1.5,
        },
        body1: {
          color: themeColors.schwarzgrau500,
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: 1.5,
        },
        body2: {
          fontSize: 12,
          color: semanticColors.text.muted,
        },
        button: {
          fontSize: "16px",
          fontWeight: "700",
          lineHeight: "19px",
          textAlign: "center",
          textTransform: "none",
        },
        indexCardTitle: {
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: "24.2px",
          textAlign: "left",
          color: themeColors.schwarzgrau900,
        },
        indexCardListItem: {
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "23px",
          color: themeColors.schwarzgrau700,
        },
        menuTitle: {
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: 1.2,
          color: themeColors.schwarzgrau900,
        },
        menuTitleLink: {
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: 1.2,
          color: themeColors.primaryMain,
        },
        pageSectionTitle: {
          fontWeight: 700,
          fontSize: "20px",
          lineHeight: 1.5,
          color: themeColors.schwarzgrau900,
        },
        tabLabel: {
          fontWeight: 700,
          fontSize: "18px",
          lineHeight: 1.5,
          letterSpacing: "-0.011em",
        },
        pageSubSectionTitle: {
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "19px",
          color: themeColors.schwarzgrau900,
        },
        headerMenuTitle: {
          fontWeight: 500,
          fontSize: "18px",
          lineHeight: "16px",
          color: themeColors.schwarzgrau900,
        },
        subText: {
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: 1.5,
          color: themeColors.schwarzgrau700,
        },
        hintText: {
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: 1.5,
          color: semanticColors.text.muted,
        },
        headerMenuSectionTitle: {
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "16px",
          color: themeColors.schwarzgrau900,
          display: "block",
        },
        headerMenuSectionText: {
          display: "block",
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: 1,
          color: themeColors.schwarzgrau700,
        },
        uploadDropFieldError: {
          fontWeight: 700,
          fontSize: "18px",
          lineHeight: "20px",
          color: semanticColors.feedback.error.dark,
        },
        uploadDropFieldDescription1: {
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "20px",
          color: themeColors.schwarzgrau700,
        },
        link: {
          fontWeight: 600,
          fontSize: "16px",
          lineHeight: "20px",
          color: themeColors.primaryMain,
          cursor: "pointer",
        },
        linkSmall: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: "16px",
          color: themeColors.primaryMain,
          cursor: "pointer",
        },
        uploadFileTableHeader: {
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "19px",
          color: themeColors.schwarzgrau700,
        },
        hint: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: 1.5,
          color: themeColors.schwarzgrau700,
        },
        tableHeader: {
          fontWeight: 700,
          fontSize: "16px",
          lineHeight: "20px",
          color: themeColors.schwarzgrau500,
        },
        tableValue: {
          fontWeight: 700,
          fontSize: "18px",
          lineHeight: "22px",
          color: themeColors.schwarzgrau700,
        },
        tableSubValue: {
          fontWeight: 700,
          fontSize: "18px",
          lineHeight: "23px",
          color: semanticColors.interactive.primaryAccent,
        },
        uploadFileStatusRunning: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: 1.5,
          color: semanticColors.feedback.warning.strong,
        },
        uploadFileStatusFailed: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: 1.5,
          color: semanticColors.feedback.error.strong,
        },
        uploadFileStatusCompleted: {
          fontWeight: 400,
          fontSize: "12px",
          lineHeight: 1.5,
          color: semanticColors.feedback.success.strong,
        },
        addressLabel: {
          fontWeight: 400,
          fontSize: "22px",
          lineHeight: 1.5,
          letterSpacing: "-0.011em",
        },
        tooltipText: {
          fontFamily: sourceSansFontFamily,
          fontSize: "12px",
          fontWeight: 500,
          lineHeight: "16px",
        },
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: semanticColors.background.paper,
              boxShadow: "0px 1px 4px 0px #0000001A",
              height: "63px",
              "& .MuiToolbar-root": {
                minHeight: "unset",
              },
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            html: {
              /* width */
              "*::-webkit-scrollbar": {
                width: scrollBarWidth,
              },
              /* Track */
              "*::-webkit-scrollbar-track": {
                background: "transparent",
              },
              /* Handle */
              "*::-webkit-scrollbar-thumb": {
                height: "40px",
                border: `5px solid rgba(0, 0, 0, 0)`,
                backgroundClip: "padding-box",
                borderRadius: "12px",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              },
              /* Handle on hover */
              "*::-webkit-scrollbar-thumb:hover": {
                border: `2px solid rgba(0, 0, 0, 0)`,
                backgroundClip: "padding-box",
                borderRadius: "12px",
                backgroundColor: "rgba(0, 0, 0, 0.4)",
              },
              "*::-webkit-scrollbar-corner": {
                background: "transparent",
              },
            },
            body: {
              backgroundColor: semanticColors.background.canvas,
            },
          },
        },
        MuiLink: {
          defaultProps: {
            component: LinkBehavior,
          } as LinkProps,
        },
        MuiButtonBase: {
          defaultProps: {
            LinkComponent: LinkBehavior,
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            },
            startIcon: {
              marginRight: 0,
              marginLeft: 0,
              "& > *:nth-of-type(1)": {
                fontSize: "20px",
              },
            },
            endIcon: {
              marginRight: 0,
              marginLeft: 0,
              "& > *:nth-of-type(1)": {
                fontSize: "20px",
              },
            },
          },
          variants: [
            {
              props: { size: "small" },
              style: {
                height: "35px",
                padding: "6px 24px 6px 24px",
                gap: "10px",
                borderRadius: borderRadius.button,
                "&:hover": {
                  height: "35px",
                  padding: "6px 24px 6px 24px",
                  gap: "10px",
                  borderRadius: borderRadius.button,
                },
              },
            },
            {
              props: { size: "medium" },
              style: {
                height: "45px",
                padding: "8px 30px 8px 30px",
                gap: "10px",
                borderRadius: borderRadius.button,
                "&:hover": {
                  height: "45px",
                  padding: "8px 30px 8px 30px",
                  gap: "10px",
                  borderRadius: borderRadius.button,
                },
              },
            },
            {
              props: { size: "large" },
              style: {
                height: "45px",
                padding: "8px 30px 8px 30px",
                gap: "10px",
                borderRadius: borderRadius.button,
                "&:hover": {
                  height: "45px",
                  padding: "8px 30px 8px 30px",
                  gap: "10px",
                  borderRadius: borderRadius.button,
                },
              },
            },
          ],
        },
        MuiCard: {
          variants: [
            {
              props: { variant: "cardObjectTile" },
              style: {
                "&.MuiCard-root": {
                  background: semanticColors.background.paper,
                  borderRadius: "10px",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "scale(1.02)",
                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                  },
                },
              },
            },
            {
              props: { variant: "loadingCardObjectTile" },
              style: {
                "&.MuiCard-root": {
                  background: semanticColors.background.paper,
                  borderRadius: "10px",
                },
              },
            },
            {
              props: { variant: "pageSection" },
              style: {
                "&.MuiCard-root": {
                  padding: "20px 17px",
                  gap: "20px",
                  background: semanticColors.background.paper,
                  borderRadius: "0px",
                },
                "& .MuiCardHeader-root": {
                  padding: 0,
                },
                "& .MuiCardContent-root": {
                  padding: 0,
                  width: "100%",
                  "&:last-child": {
                    padding: 0,
                  },
                },
                "& .MuiCardHeader-action": {
                  margin: 0,
                },
              },
            },
          ],
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: "100px",
              fontSize: "12px",
              fontWeight: "700",
              lineHeight: "16px",
              textAlign: "right",
            },
          },
          variants: [
            {
              props: { color: "inactive" },
              style: {
                background: semanticColors.state.inactive,
                color: semanticColors.text.inverse,
              },
            },
            {
              props: { color: "primary" },
              style: {
                color: semanticColors.text.inverse,
              },
            },
            {
              props: { color: "invited" },
              style: {
                color: themeColors.schwarzgrau700,
                background: semanticColors.surface.invited,
              },
            },
            {
              props: { variant: "playgroundFilterChip" },
              style: {
                border: `1px solid ${semanticColors.border.subtle}`,
                borderRadius: "4px",
                background: semanticColors.background.paper,
                color: themeColors.primaryMain,
                "& .MuiChip-deleteIcon": {
                  color: semanticColors.interactive.primaryAccent,
                  "&:hover": {
                    color: darken(semanticColors.interactive.primaryAccent, 0.2),
                  },
                },
              },
            },
          ],
        },
        CheckboxField: {
          styleOverrides: {
            root: ({ ownerState, theme }) => ({
              padding: "12px 13px",
              margin: 0,
              width: "100%",
              gap: "10px",
              borderRadius: "4px",
              background: ownerState.checked ? theme.palette.action.selected : theme.palette.common.white,
              border: ownerState.checked
                ? `1px solid ${theme.palette.primary.light}`
                : `1px solid ${theme.palette.common.white}`,
            }),
          },
        },
        RadioField: {
          styleOverrides: {
            root: ({ ownerState, theme }) => ({
              padding: "12px 13px",
              margin: 0,
              width: "100%",
              gap: "10px",
              borderRadius: "4px",
              background: ownerState.selected ? theme.palette.action.selected : theme.palette.common.white,
              border: ownerState.selected
                ? `1px solid ${theme.palette.primary.light}`
                : `1px solid ${theme.palette.common.white}`,
            }),
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              margin: "6px",
              padding: "10px",
              fontFamily: sourceSansFontFamily,
              fontSize: "12px",
              fontWeight: "500",
              lineHeight: "16px",
              boxShadow: "0px 3px 16px 0px rgba(0, 0, 0, 0.5)",
            },
          },
        },
        MuiTableContainer: {
          styleOverrides: {
            root: {
              borderRadius: 0,
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              "&:nth-of-type(odd)": {
                backgroundColor: semanticColors.surface.blueBg,
              },
              "&:nth-of-type(even)": { backgroundColor: semanticColors.background.paper },
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              border: "none",
            },
          },
        },
        MuiToggleButton: {
          styleOverrides: {
            root: {
              border: "1px solid !important",
              borderRadius: "4px !important",
              "&.Mui-selected": {
                backgroundColor: semanticColors.interactive.primary,
                color: semanticColors.text.inverse,
              },
            },
          },
        },
        MuiPaginationItem: {
          styleOverrides: {
            root: {
              minWidth: "40px",
              height: "40px",
              fontSize: "14px",
              borderRadius: "50%",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            },
            ellipsis: {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            },
          },
        },
      },
    },
    ...args,
  );

export const WidgetTheme = (...args: object[]): Theme => {
  const baseTheme = LightTheme(...args);

  return createTheme(baseTheme, {
    analysisLayout: {
      ...baseTheme.analysisLayout,
      ...widgetAnalysisLayoutOverrides,
    },
    typography: {
      h4: {
        ...baseTheme.typography.h4,
        fontSize: "15px",
      },
      pageTitle: {
        ...baseTheme.typography.pageTitle,
        fontSize: "16px",
      },
      widgetTitle: {
        ...baseTheme.typography.widgetTitle,
        fontSize: "16px",
      },
      sectionHeader: {
        ...baseTheme.typography.sectionHeader,
        fontSize: "16px",
      },
      modalHeader: {
        ...baseTheme.typography.modalHeader,
        fontSize: "16px",
      },
      pageSectionTitle: {
        ...baseTheme.typography.pageSectionTitle,
        fontSize: "16px",
      },
      inputLabel: {
        ...baseTheme.typography.inputLabel,
        fontSize: "14px",
      },
      body1: {
        ...baseTheme.typography.body1,
        fontSize: "14px",
      },
      hint: {
        ...baseTheme.typography.hint,
        fontSize: "13px",
      },
      button: {
        ...baseTheme.typography.button,
        fontSize: "14px",
      },
    },
  });
};
